import { Copy, XCircle } from 'lucide-react';
import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import LinqOneQR from '../../assets/qr-verifications/linqone/linqoneqr-success_qr.png';
import NeuhausQR from '../../assets/qr-verifications/chocolate/NeuhausQr.png';
import FictionalChocolateQR from '../../assets/qr-verifications/chocolate-fictional/chocolate-fictional-successQR.png';
import BottomNavbar from '../../layouts/BottomNavbar';
import TopBar from '../../layouts/TopBar';
import MenuSidebar from '../../layouts/MenuSidebar';
import { darkTheme, lightTheme } from '../../theme/theme';
import { getActiveProduct, PRODUCTS } from '../../utils/urlConfig';
import useTheme from '../../hooks/useTheme';
import LoadingSpinner from '../../components/common/LoadingSpinner';

const GS1DLScreen = () => {
    const [isDark, setIsDark] = useTheme();
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const [showUrlPopup, setShowUrlPopup] = useState(false);
    const [copied, setCopied] = useState(false);
    const [activeProduct, setActiveProductState] = useState('');
    const navigate = useNavigate();
    const { productId: urlProductId, serialNumber: urlSerialNumber } = useParams();
    const theme = isDark ? darkTheme : lightTheme;
    
    useEffect(() => {
        if (urlProductId && urlSerialNumber) {
            let product = getActiveProduct();
            for (const [key, config] of Object.entries(PRODUCTS)) {
                if (config.gtin === urlProductId) {
                    product = key;
                    break;
                }
            }
            setActiveProductState(product);
        } else {
            setActiveProductState(getActiveProduct());
        }
    }, [urlProductId, urlSerialNumber]);

    const productConfig = PRODUCTS[activeProduct] || PRODUCTS[getActiveProduct()];

    // Build dynamic GS1 Digital Link URL based on active product
    // Use current window location as base URL (e.g., http://localhost:3000 or https://mobile-dlinq.h2cloud.it)
    const baseUrl = window.location.origin;
    const gtin = urlProductId || productConfig?.gtin || "00000000000000";
    const productSerialNumber = urlSerialNumber || productConfig?.serialNumber || "UNKNOWN";
    const fullUrl = `${baseUrl}/01/${gtin}/21/${productSerialNumber}`;

    // Copy URL function
    const handleCopyUrl = () => {
        navigator.clipboard.writeText(fullUrl);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };

    
    // Get product-specific QR image based on active product
    const qrImageMap = {
        'linqOne': LinqOneQR,
        'chocolate': NeuhausQR,
        'chocolateFictional': FictionalChocolateQR
    };
    const qrImage = qrImageMap[activeProduct] || LinqOneQR;

    return (
        <div className="h-screen flex flex-col" style={{ backgroundColor: theme.background1 }}>
            {/* Header */}
            <TopBar
                theme={theme}
                isDark={isDark}
                setIsDark={setIsDark}
                title="GS1 DL"
                onBackClick={() => {
                    const baseUrl = urlProductId && urlSerialNumber 
                        ? `/01/${urlProductId}/21/${urlSerialNumber}/verification`
                        : `/01/${productConfig?.gtin}/21/${productConfig?.serialNumber}/verification`;
                    
                    // Retrieve and append the last verification status
                    const savedStatus = localStorage.getItem('lastVerificationStatus');
                    const backPath = savedStatus ? `${baseUrl}/${savedStatus}` : baseUrl;
                    
                    navigate(backPath, { state: { isDark } });
                }}
                isMenuOpen={isMenuOpen}
                setIsMenuOpen={setIsMenuOpen}
            />

            {/* Menu Sidebar */}
            <MenuSidebar
                theme={theme}
                isDark={isDark}
                isOpen={isMenuOpen}
                onClose={() => setIsMenuOpen(false)}
            />

            {/* Scrollable Content */}
            <div className="flex-1 overflow-y-auto px-4 pt-20 pb-24">
                {!activeProduct && <LoadingSpinner />}
                {/* qr */}
                <div className="w-full mt-4 flex items-center justify-center px-2">
                    <div className="flex items-center justify-center bg-white rounded-lg p-3 w-[180px] sm:w-[280px] md:w-[350px] lg:w-[400px]">
                        <img src={qrImage} alt="QR Code" className="w-full h-auto object-contain" />
                    </div>
                </div>

                {/* link */}
                <div 
                    className="flex flex-row items-center justify-center text-center px-8 py-2 w-full mt-8 h-auto rounded-full cursor-pointer hover:opacity-80 transition-opacity overflow-hidden"
                    style={{ backgroundColor: "#BBE3F5" }}
                    onClick={() => setShowUrlPopup(true)}
                >
                    <p className="font-regular text-ellipsis whitespace-nowrap overflow-hidden" style={{ color: "#009ADA", maxWidth: "100%" }}>{fullUrl}</p>
                </div>

                {/* Product Details */}
                <div className="flex flex-col items-start justify-start text-left px-4 py-4 w-full mt-4 h-auto rounded-lg gap-4"
                    style={{ backgroundColor: theme.background2, borderColor: theme.stroke, borderWidth: 1 }}>
                    <div className="border-b w-full pb-2 mb-1" style={{ borderColor: theme.stroke }}>
                        <p className="text-sm" style={{ color: theme.gray }}>Base URL:</p>
                        <p className="font-regular" style={{ color: theme.text }}>{baseUrl}</p>
                    </div>
                    <div className="border-b w-full pb-2 mb-1" style={{ borderColor: theme.stroke }}>
                        <p className="text-sm" style={{ color: theme.gray }}>Product Key:</p>
                        <p className="font-regular" style={{ color: theme.text }}>01</p>
                    </div>
                    <div className="border-b w-full pb-2 mb-1" style={{ borderColor: theme.stroke }}>
                        <p className="text-sm" style={{ color: theme.gray }}>Product GTIN:</p>
                        <p className="font-regular" style={{ color: theme.text }}>{gtin}</p>
                    </div>
                    <div className="border-b w-full pb-2 mb-1" style={{ borderColor: theme.stroke }}>
                        <p className="text-sm" style={{ color: theme.gray }}>Serial Key:</p>
                        <p className="font-regular" style={{ color: theme.text }}>21</p>
                    </div>
                    <div className="mb-2">
                        <p className="text-sm" style={{ color: theme.gray }}>Serial Number:</p>
                        <p className="font-regular" style={{ color: theme.text }}>{productSerialNumber}</p>
                    </div>
                </div>
            </div>

            {/* Bottom Navigation */}
            <BottomNavbar theme={theme} isDark={isDark} />

            {/* URL Popup */}
            {showUrlPopup && (
                <div className="fixed inset-0 z-[9999] flex items-center justify-center px-6" style={{ backgroundColor: 'rgba(0, 0, 0, 0.5)' }}>
                    <div className="w-full max-w-md rounded-lg p-6" style={{ backgroundColor: theme.background2 }}>
                        {/* Header */}
                        <div className="flex items-center justify-between mb-4">
                            <h2 className="text-lg font-semibold" style={{ color: theme.text }}>Copy URL</h2>
                            <button
                                onClick={() => setShowUrlPopup(false)}
                                className="p-1"
                                style={{ color: theme.gray }}
                            >
                                <XCircle size={24} />
                            </button>
                        </div>
                        
                        {/* URL Display */}
                        <div className="mb-4 p-3 rounded-lg break-all" style={{ backgroundColor: theme.background1, color: theme.text }}>
                            {fullUrl}
                        </div>
                        
                        {/* Copy Button */}
                        <button
                            onClick={handleCopyUrl}
                            className="w-full py-3 rounded-lg font-semibold flex items-center justify-center gap-2 transition-all hover:opacity-90"
                            style={{ backgroundColor: theme.button, color: '#ffffff' }}
                        >
                            <Copy size={18} />
                            {copied ? 'Copied!' : 'Copy URL'}
                        </button>
                    </div>
                </div>
            )}

        </div>
    );
};

export default GS1DLScreen;